import React, {
    useCallback,
    useEffect,
    useMemo,
    useRef,
    useState,
} from 'react';

import {
    FlatList,
    InteractionManager,
    ListRenderItem,
    View,
} from 'react-native';

import { useTranslation } from 'react-i18next';
import { useDispatch, useSelector } from 'react-redux';

import { useNavigation } from '@react-navigation/core';
import { useFocusEffect } from '@react-navigation/native';
import { StackNavigationProp } from '@react-navigation/stack';
import {
    endOfDay,
    millisecondsToSeconds,
    secondsToMilliseconds,
    startOfDay,
} from 'date-fns';
import debounce from 'lodash.debounce';

import { EPGProgram } from 'custom_modules/react-native-epg';

import { LANGUAGE_KEYS } from 'config/languageKeys';

import { SCREEN } from 'router/screens';
import { RouteNavigationParams } from 'router/types';

import {
    selectAppState,
    selectScreenReaderEnabled,
} from 'store/config/selectors';
import {
    getEpgByVisibleChannels,
    setEpgDate,
    setEpgRequestInitialStatus,
} from 'store/epg/actions';
import {
    selectEpgParams,
    selectEpgProgramsByChannelsIds,
    selectEpgRequestStatus,
    selectPreviouslyWatchedChannel,
} from 'store/epg/selectors';
import { showPlayer } from 'store/player/actions';
import { StreamTypes } from 'store/player/actions.types';
import { selectChannelsListWithTvChannel } from 'store/programs/selectors';
import { EpgProgram } from 'store/railsBuilder/reducer.types';
import { selectEpgTemplateId } from 'store/railsBuilder/selectors';
import { selectCachedRecordingsByEventId } from 'store/recordings/selectors';
import { startSentryTransaction } from 'store/sentry/actions';
import { SENTRY_TRANSACTIONS } from 'store/sentry/constants';
import {
    selectActiveUserProfile,
    selectEntitlements,
} from 'store/user/selectors';
import { requestIdle, requestPending, requestSuccess } from 'store/utils';

import isCatchupChannel from 'services/data-manager/catchup/isCatchupChannel';
import isCatchupEpgProgram from 'services/data-manager/catchup/isCatchupEpgProgram';
import { TECHNICAL_DEVICE_TYPE } from 'services/data-manager/constants';
import parseVideoItem from 'services/data-manager/parsing/parseVideoItem';
import useGetDeepLinkTriggeredParams from 'services/uav/useGetDeepLinkTriggeredParams';
import useThirdPartyAppStartParams from 'services/uav/useGetThirdPartyAppStartParams';
import { logUavAnalytics } from 'services/uav/utils';

import { useCast } from 'utils/cast/useCast';
import usePrevious from 'utils/hooks/usePrevious';
import useProfileParentalRating from 'utils/hooks/useProfileParentalRating';
import isValidPastDeeplinkItem from 'utils/isValidPastDeeplinkItem';
import openDeeplink from 'utils/openDeeplink';
import needsToLockContent from 'utils/parentalControl/needsToLockContent';
import { isWeb } from 'utils/responsiveness';

import { DetailsRequestUrlType } from 'screens/Detail/types';
import useChannels from 'screens/Epg/hooks/useChannels';
import useFindLiveProgram from 'screens/Epg/hooks/useFindLiveProgram';
import { EpgFilterCategory, EpgProgramExtra } from 'screens/Epg/types';
import parseRBProgramToEpgProgram from 'screens/Epg/utils/parseRBProgramToEpgProgram';
import { isLive, isLiveEpgProgram } from 'screens/Epg/utils/program';
import useParentalControl from 'screens/PlayerModal/hooks/useParentalControl';

import usePlayerState from 'components/EmbeddedPlayer/hooks/usePlayerState';
import GenericPinDialog from 'components/GenericPinDialog';
import {
    DeepLinkTriggered,
    ThirdPartyAppStartItem,
} from 'components/Ribbon/types';
import Text, { ETextCategory, ETextColor } from 'components/Text';

import EpgProgramCard from '../EpgProgramCard';
import EpgProgramListHeader from '../EpgProgramListHeader';
import EpgProgramListSkeleton from '../EpgProgramListSkeleton';
import styles from './styles';

const {
    EPG: { NEW_EPG_LAYOUT_NO_PROGRAM },
} = LANGUAGE_KEYS;

type Props = {
    selectedFilterCategory?: EpgFilterCategory;
};

const EpgProgramList = ({ selectedFilterCategory }: Props) => {
    const flatlistRef = useRef<FlatList>(null);
    const channelChangedRef = useRef<boolean>(false);
    const scrollToProgramTimeoutRef = useRef<NodeJS.Timeout>(undefined);
    const dispatch = useDispatch();
    const navigation =
        useNavigation<StackNavigationProp<RouteNavigationParams, SCREEN.EPG>>();

    const appState = useSelector(selectAppState);
    const channelsList = useSelector(selectChannelsListWithTvChannel);
    const previouslyWatchedChannels = useSelector(
        selectPreviouslyWatchedChannel,
    );
    const selectedProfileId = useSelector(selectActiveUserProfile);
    const programsByChannelsIds = useSelector(selectEpgProgramsByChannelsIds);
    const { start: epgDataStartTime } = useSelector(selectEpgParams);
    const recordingsByEventId = useSelector(selectCachedRecordingsByEventId);
    const entitlements = useSelector(selectEntitlements);
    const templateId = useSelector(selectEpgTemplateId);
    const epgRequestStatus = useSelector(selectEpgRequestStatus);
    const screenReaderEnabled = useSelector(selectScreenReaderEnabled);

    const startTime = secondsToMilliseconds(epgDataStartTime);
    const channels = useChannels({
        channels: channelsList,
        filterCategory: selectedFilterCategory,
        entitlements,
    });
    const onFindLiveProgram = useFindLiveProgram(
        startTime,
        programsByChannelsIds,
    );
    const profileParentalControlRating = useProfileParentalRating();

    const {
        showPinDialog,
        onShowPinDialog,
        onParentalCheckDismiss,
        onParentalControlChecked,
    } = useParentalControl({
        fullscreen: false,
    });
    const { getThirdPartyAppStartParams } = useThirdPartyAppStartParams();
    const { getDeepLinkTriggeredParams } = useGetDeepLinkTriggeredParams();
    const { isCastConnected, cast } = useCast();

    const { t } = useTranslation();

    const [showGradient, setShowGradient] = useState(false);
    const [selectedProgramData, setSelectedProgramData] =
        useState<EpgProgram | null>(null);

    const previouslyWatchedChannel = selectedProfileId
        ? previouslyWatchedChannels[selectedProfileId]
        : undefined;

    const selectedEpgChannel = useMemo(
        () =>
            channels.find(({ id }) => id === previouslyWatchedChannel) ||
            channels[0],
        [channels, previouslyWatchedChannel],
    );

    const selectedChannel = useMemo(
        () =>
            channelsList.find(
                ({ editorial: { id } }) => id === selectedEpgChannel?.id,
            ) || channelsList[0],
        [channelsList, selectedEpgChannel?.id],
    );

    const prevSelectedChannelId = usePrevious(selectedChannel?.editorial?.id);
    const prevEpgStartTime = usePrevious(epgDataStartTime);
    const channelSupportsCatchup =
        !!selectedChannel?.editorial && isCatchupChannel(selectedChannel);
    const channelSupportsStartover =
        !!selectedChannel?.editorial?.startOverSupport &&
        !!selectedChannel?.technical?.startOverSupport;

    const {
        playerState: { playbackType },
    } = usePlayerState({
        videoItem: undefined,
        playbackType: StreamTypes.live,
    });

    const programs = useMemo(
        () =>
            programsByChannelsIds?.[selectedEpgChannel.id]?.map((item) => {
                const recordingItem = recordingsByEventId[item.id];
                const catchupAllowed =
                    channelSupportsCatchup && isCatchupEpgProgram(item);
                const startoverAllowed =
                    channelSupportsStartover && item.isStartOver;
                const tvChannel = selectedChannel?.editorial?.tvChannel;
                return parseRBProgramToEpgProgram({
                    t,
                    subscribed: selectedEpgChannel?.extra?.subscribed ?? false,
                    program: item,
                    selectedDate: startTime,
                    catchupAllowed,
                    startoverAllowed,
                    tvChannel,
                    recordingItem,
                    shouldHideShortDuration: false,
                });
            }) ?? [],
        [
            selectedChannel,
            selectedEpgChannel,
            programsByChannelsIds,
            channelSupportsCatchup,
            channelSupportsStartover,
            recordingsByEventId,
            startTime,
            t,
        ],
    );

    const handleFetchProgramsForChannels = debounce(
        () => {
            dispatch(
                getEpgByVisibleChannels({
                    channels: [selectedEpgChannel.id],
                    withLoading: true,
                }),
            );
        },
        1000,
        { trailing: false, leading: true },
    );

    const onDeepLinkTriggered = useCallback(
        (item: DeepLinkTriggered) => {
            if (templateId) {
                logUavAnalytics(
                    getDeepLinkTriggeredParams({
                        templateId,
                        ...item,
                    }),
                );
            }
        },
        [templateId, getDeepLinkTriggeredParams],
    );

    const onThirdPartyAppStart = useCallback(
        ({
            appReference,
            deeplinkProviderId,
            deepLinkid,
            providerResourceId,
            appName,
        }: ThirdPartyAppStartItem) => {
            logUavAnalytics(
                getThirdPartyAppStartParams({
                    railId: undefined,
                    playbackType,
                    templateId,
                    appReference,
                    deeplinkProviderId,
                    deepLinkid,
                    providerResourceId,
                    appName,
                }),
            );
        },
        [templateId, playbackType, getThirdPartyAppStartParams],
    );

    const navigateToDetailPage = useCallback(
        (programData: EpgProgram) => {
            let params: RouteNavigationParams[SCREEN.DETAILS] = {
                templateId,
                filterType: DetailsRequestUrlType.PROGRAM,
                id: programData.id,
                channelId: programData.serviceRef,
            };

            if (programData.seriesRef) {
                const programId = programData.editorial.technicals?.find(
                    ({ deviceType }) =>
                        deviceType.includes(TECHNICAL_DEVICE_TYPE),
                )?.ProgramId;

                params = {
                    ...params,
                    seriesId: programData.seriesRef,
                    season: programData.SeasonNumber,
                    episode: programData.episodeNumber,
                    programId,
                };
            }

            dispatch(
                startSentryTransaction(SENTRY_TRANSACTIONS.DetailPageRendering),
            );
            navigation.navigate(SCREEN.DETAILS, params);
        },
        [templateId, navigation],
    );

    const handleProgramPress = useCallback(
        (program: EPGProgram<EpgProgramExtra>) => {
            const programData = programsByChannelsIds[program.channelId].find(
                ({ id }) => id === program.id,
            );

            if (programData && !isLiveEpgProgram(programData)) {
                const ableToPlayCatchup =
                    isCatchupEpgProgram(programData) && isCatchupChannel;

                if (
                    !ableToPlayCatchup &&
                    isValidPastDeeplinkItem(programData)
                ) {
                    openDeeplink(
                        programData,
                        onDeepLinkTriggered,
                        onThirdPartyAppStart,
                    );
                } else if (
                    needsToLockContent(
                        profileParentalControlRating,
                        program.extra?.rating,
                    )
                ) {
                    setSelectedProgramData(programData);
                    onShowPinDialog();
                } else {
                    navigateToDetailPage(programData);
                }

                return;
            }

            const videoItem = parseVideoItem({
                channel: selectedChannel,
                program: programData,
            });

            if (isCastConnected) {
                cast(videoItem, StreamTypes.live);
            } else {
                dispatch(
                    startSentryTransaction(SENTRY_TRANSACTIONS.ChannelTune),
                );
                dispatch(
                    showPlayer({
                        videoItem,
                        playbackType,
                        railId: undefined,
                        templateId,
                    }),
                );
            }
        },
        [
            navigateToDetailPage,
            onDeepLinkTriggered,
            onShowPinDialog,
            onThirdPartyAppStart,
            profileParentalControlRating,
            programsByChannelsIds,
            cast,
            isCastConnected,
            playbackType,
            selectedChannel,
            templateId,
        ],
    );

    const handleCloseParentalControl = useCallback(() => {
        onParentalCheckDismiss(isLive(selectedProgramData));
        setSelectedProgramData(null);
    }, [onParentalCheckDismiss, selectedProgramData]);

    const handleParentalControlChecked = useCallback(() => {
        if (selectedProgramData) {
            navigateToDetailPage(selectedProgramData);
        }

        onParentalControlChecked();
        setSelectedProgramData(null);
    }, [selectedProgramData, navigateToDetailPage, onParentalControlChecked]);

    const handleListScroll = useCallback(
        ({ nativeEvent: { contentOffset } }) => {
            setShowGradient(contentOffset.y > 0);
        },
        [],
    );

    const findLiveProgramTimoutRef = useRef<NodeJS.Timeout | null>(null);

    const handleFindLiveProgram = (
        _programs: EPGProgram<EpgProgramExtra>[],
    ) => {
        if (scrollToProgramTimeoutRef.current != null) {
            clearTimeout(scrollToProgramTimeoutRef.current);
            scrollToProgramTimeoutRef.current = undefined;
        }

        if (findLiveProgramTimoutRef.current) {
            clearTimeout(findLiveProgramTimoutRef.current);
            findLiveProgramTimoutRef.current = null;
        }

        findLiveProgramTimoutRef.current = setTimeout(async () => {
            if (selectedChannel) {
                const liveProgram = await onFindLiveProgram(selectedChannel);

                if (liveProgram) {
                    const index = _programs?.findIndex(
                        (program) => program.id === liveProgram.editorial.id,
                    );

                    dispatch(setEpgRequestInitialStatus());

                    if (index >= 0) {
                        if (scrollToProgramTimeoutRef.current != null) {
                            clearTimeout(scrollToProgramTimeoutRef.current);
                            scrollToProgramTimeoutRef.current = undefined;
                        }

                        scrollToProgramTimeoutRef.current = setTimeout(
                            () => {
                                flatlistRef.current?.scrollToIndex({
                                    index: index < _programs.length ? index : 0,
                                    viewOffset: 50,
                                });
                            },
                            screenReaderEnabled || isWeb ? 500 : 100,
                        );
                    } else {
                        setShowGradient(false);
                    }
                }
            }
        }, 500);
    };

    const renderProgram = useCallback<
        ListRenderItem<EPGProgram<EpgProgramExtra>>
    >(
        ({ item }) => (
            <EpgProgramCard
                program={item}
                onPress={handleProgramPress}
                wasChannelChanged={channelChangedRef.current}
            />
        ),
        [handleProgramPress],
    );

    const handleScrollToIndexFailed = useCallback(() => {
        flatlistRef.current?.scrollToIndex({
            index: 0,
            animated: false,
        });
    }, []);

    const renderContent = useCallback(() => {
        if (programs?.length) {
            return (
                <FlatList
                    ref={flatlistRef}
                    style={styles.list}
                    contentContainerStyle={styles.contentContainer}
                    data={programs}
                    renderItem={renderProgram}
                    snapToAlignment="start"
                    decelerationRate="fast"
                    keyboardDismissMode="on-drag"
                    overScrollMode="never"
                    bounces={false}
                    accessibilityRole="menu"
                    showsVerticalScrollIndicator={false}
                    initialNumToRender={programs.length}
                    onScroll={handleListScroll}
                    onScrollToIndexFailed={handleScrollToIndexFailed}
                />
            );
        }

        if (requestSuccess(epgRequestStatus)) {
            return (
                <View style={styles.emptyProgramsContainer}>
                    <Text
                        category={ETextCategory.title2}
                        color={ETextColor.neutral}
                        center
                    >
                        {t(NEW_EPG_LAYOUT_NO_PROGRAM)}
                    </Text>
                </View>
            );
        }

        return null;
    }, [
        programs,
        epgRequestStatus,
        renderProgram,
        handleListScroll,
        handleScrollToIndexFailed,
        t,
    ]);

    useEffect(() => {
        const now = new Date();

        dispatch(
            setEpgDate({
                start: millisecondsToSeconds(startOfDay(now).getTime()),
                end: millisecondsToSeconds(endOfDay(now).getTime()),
            }),
        );
    }, [dispatch]);

    useEffect(() => {
        if (
            !programs?.length &&
            (prevSelectedChannelId !== selectedChannel?.editorial.id ||
                prevEpgStartTime !== epgDataStartTime)
        ) {
            handleFetchProgramsForChannels();
        }

        if (
            !!prevSelectedChannelId &&
            prevSelectedChannelId !== selectedChannel?.editorial.id
        ) {
            channelChangedRef.current = true;
        }
    }, [
        programs,
        handleFetchProgramsForChannels,
        selectedChannel?.editorial.id,
        prevSelectedChannelId,
        epgDataStartTime,
        prevEpgStartTime,
    ]);

    useEffect(() => {
        if (!isWeb && programs.length) {
            InteractionManager.runAfterInteractions(() => {
                handleFindLiveProgram(programs);
            });
        }
    }, [programs.length]);

    useFocusEffect(
        useCallback(() => {
            if (isWeb && programs.length) {
                requestAnimationFrame(() => {
                    handleFindLiveProgram(programs);
                });
            }
        }, [programs.length]),
    );

    useEffect(() => {
        setShowGradient(false);
    }, [selectedChannel?.editorial.id]);

    useEffect(() => {
        channelChangedRef.current = false;
    }, [selectedFilterCategory]);

    return (
        <View style={styles.root} accessible={false}>
            {requestPending(epgRequestStatus) || !selectedEpgChannel ? (
                <EpgProgramListSkeleton />
            ) : (
                <>
                    {requestSuccess(epgRequestStatus) ||
                    (requestIdle(epgRequestStatus) && programs?.length) ? (
                        <EpgProgramListHeader
                            title={selectedChannel?.editorial.longName}
                            showGradient={showGradient}
                        />
                    ) : null}
                    {renderContent()}
                </>
            )}

            <GenericPinDialog
                modal
                appState={appState}
                visible={showPinDialog}
                onClose={handleCloseParentalControl}
                onSuccess={handleParentalControlChecked}
            />
        </View>
    );
};

export default EpgProgramList;
